import { Avatar, CardContent, Grid, Pagination, Typography } from "@mui/material";
import { APP_PAGE_SIZE_DEFAULT } from "app/appConstants";
import { MprcCard, MprcCardActions, MprcConfirmDialog, MprcContainer, MprcIconButtonMoreOptionsDropdown, MprcJoinComponents, MprcLink, MprcRequestErrorReport, MprcRequestIndicator, MprcSpinnerIndicator } from "components";
import { MprcFilterCount } from "components/filter";
import { MprcDropdownBuildGroupedItems, MprcDropdownItems } from "components/MprcIconButtonDropdown";
import { MprcListItemTitle } from "components/MprcListItemTitle";
import { MprcSearchTextFieldHandle } from "components/MprcSearchTextField";
import { ManagerEfficacyUserOrderBy, ManagerEfficacyUserResponse, ManagerEfficacyUserResponsePaginatedList } from "generated/CoreApi_v1";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import { usePageTitle } from "hooks/usePageTitle";
import { Formaters } from "lib/Formaters";
import moment from "moment";
import { useCoreApi } from "providers/CoreApiProvider";
import { useModalUserProfile } from "providers/UserProfileProvider";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Filter } from "views/assessment/managerEfficacy/ListUsersManagerEfficacyFilter";
import { SelectEfficacySurveyDrawer } from "views/assessment/managerEfficacy/SelectEfficacySurveyDrawer";
import { AssPlayerSurvey } from "views/assessment/player/AssPlayerSurvey";

export interface UsersManagerEfficacyOptions {
    searchText: string;
    orderBy: ManagerEfficacyUserOrderBy;
}

interface IViewDataFilter {
    pageNumber: number;
    filter: UsersManagerEfficacyOptions;
}

type UsersManagerEfficacyMenuOptions = "decline";

////////////////////////////////////////////////////////////////////////////

export const ListUsersManagerEfficacy: React.FC = () => {

    const api = useCoreApi();

    const { t } = useTranslation("assessment");
    const { showUser } = useModalUserProfile();

    const [viewData, setViewData] = useState<ManagerEfficacyUserResponsePaginatedList | null>(null);
    const [viewError, setViewError] = useState<any>();
    const [loading, setLoading] = useState<boolean>(true);
    const [posting, setPosting] = useState<boolean>(false);
    const [dialog, setDialog] = useState<any>();
    const [viewFilter, setViewFilter] = useState<IViewDataFilter>({
        pageNumber: 1,
        filter: {
            searchText: "",
            orderBy: ManagerEfficacyUserOrderBy.Older
        }
    });
    const searchTextField = useRef<MprcSearchTextFieldHandle | null>(null);

    ////////////////////////////////////////////////////////////////////////////

    usePageTitle(t("ManagerEfficacy.List.Title"));

    const filtered = viewFilter.filter.searchText.length > 0;

    ///
    const resetFilter = () => {
        searchTextField.current?.reset();
        setViewFilter({
            pageNumber: 1,
            filter: {
                searchText: "",
                orderBy: ManagerEfficacyUserOrderBy.Older
            }
        });
    };

    ///
    const reload = () => {
        if (viewFilter.pageNumber > 1 && viewData?.items.length == 1)
            setViewFilter({ ...viewFilter, pageNumber: 1 });
        else
            setLoading(true);
    };

    ///
    const getMenuItems = useCallback((): MprcDropdownItems<UsersManagerEfficacyMenuOptions> => {
        const group0: MprcDropdownItems<UsersManagerEfficacyMenuOptions> = [
            { text: t("ManagerEfficacy.List.DeclineAction"), value: "decline" },
        ];

        return MprcDropdownBuildGroupedItems<UsersManagerEfficacyMenuOptions>([group0]);
    }, []);

    const decline = async (item: ManagerEfficacyUserResponse) => {
        try {
            setPosting(true);
            const result = await api.v1.managerEfficacy.decline({ userId: item.userId });
            if (result.data) {
                reload();
            }
        }
        catch (e) {
            setViewError(e);
        }
        finally {
            setPosting(false);
        }
    };

    ///
    const handleSelectMenuOption = (option: UsersManagerEfficacyMenuOptions, item: ManagerEfficacyUserResponse) => {
        switch (option) {
            case "decline":
                setDialog(<MprcConfirmDialog
                    open
                    severity="danger"
                    message={<span>{t("ManagerEfficacy.List.ConfirmDeclineMessage")}</span>}
                    onClose={
                        (result) => {
                            if (result) {
                                decline(item);
                            }
                            setDialog(null);
                        }
                    }
                />);
                break;
        }
    };

    ///
    const handleUserClick = (item: ManagerEfficacyUserResponse) => {
        if (item.totalDeploys == 1 && item.assessmentId && item.deployId && item.deployParticipantId) {
            setDialog(
                <AssPlayerSurvey
                    assessmentId={item.assessmentId}
                    deployId={item.deployId}
                    deployParticipantId={item.deployParticipantId}
                    mode="play"
                    onClose={(success) => {
                        if (success) reload();
                        setDialog(null);
                    }}
                />
            );
        } else {
            setDialog(
                <SelectEfficacySurveyDrawer
                    userId={item.userId}
                    title={item.name}
                    onClose={(selected) => {
                        if (selected) {
                            setDialog(
                                <AssPlayerSurvey
                                    assessmentId={selected.assessmentId}
                                    deployId={selected.deployId}
                                    deployParticipantId={selected.deployParticipantId}
                                    mode="play"
                                    onClose={(success) => {
                                        if (success) reload();
                                        setDialog(null);
                                    }}
                                />
                            );
                        } else {
                            setDialog(null);
                        }
                    }}
                />
            );
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        async function getData() {
            try {
                const result = await api.v1.managerEfficacy.managerEfficacyUsers({
                    searchText: viewFilter.filter.searchText ?? null,
                    orderBy: viewFilter.filter.orderBy,
                    pageNumber: viewFilter.pageNumber,
                    pageSize: APP_PAGE_SIZE_DEFAULT
                });

                setViewData(result.data);
            }
            catch (e) {
                setViewData(null);
                setViewError(e);
            }
            finally {
                setLoading(false);
            }
        }

        if (loading) getData();

    }, [loading]);

    /////
    useDidUpdateEffect(() => {
        setLoading(true);
    }, [viewFilter.filter]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    ///
    const buildSecondLine = (item: ManagerEfficacyUserResponse) => {
        let c: React.ReactNode[] = [];

        if (item.totalDeploys > 1) {
            c.push(
                <MprcLink onClick={() => handleUserClick(item)}>
                    <Typography variant="caption">{t("ManagerEfficacy.List.TotalDeploys", { count: item.totalDeploys })}</Typography>
                </MprcLink>
            );
        } else {
            c.push(
                <MprcLink onClick={() => handleUserClick(item)}>
                    <Typography variant="caption">{item.trainingName}</Typography>
                </MprcLink>
            );
        }

        if (!!item.validUntil) {
            if (item.validUntil < moment().toDate()) {
                c.push(<Typography variant="bodyNotice">{t("ManagerEfficacy.List.DelayedText")}</Typography>);
            } else {
                c.push(<Typography variant="caption">{t("ManagerEfficacy.List.AnswerAtText", { name: Formaters.performaDate(item.validUntil, { excludeTime: true, preposition: "none" }) })}</Typography>);
            }
        }

        return <Typography variant="caption"><MprcJoinComponents components={c} separator={<span>&nbsp;&bull;&nbsp;</span>} /></Typography>;
    };

    ///
    const renderContent = () => {
        return (
            <>
                <CardContent>

                    {loading && <MprcSpinnerIndicator />}

                    {
                        viewData && (
                            <>
                                <MprcContainer>
                                    <MprcFilterCount
                                        loading={loading}
                                        isEmpty={viewData!.totalRows == 0}
                                        countText={t("ManagerEfficacy.List.RecordsFoundText", { count: viewData!.totalRows })}
                                        filter={{
                                            active: filtered,
                                            text: t("ManagerEfficacy.List.RecordsFilteredText", { count: viewData!.totalRows }),
                                            onResetFilter: resetFilter
                                        }}
                                    />
                                </MprcContainer>

                                <Grid container justifyContent="center" alignItems="center" spacing={2}>
                                    {viewData!.items.map((item, index) => {
                                        return (
                                            <Grid key={index} alignItems="center" justifyContent="center" size={{ xs: 12 }}>
                                                <Grid container justifyContent="flex-start" alignItems="flex-start" spacing={1} wrap="nowrap">
                                                    <Grid>
                                                        <MprcLink onClick={() => showUser(item.userId)}>
                                                            <Avatar
                                                                sx={{ width: 48, height: 48 }}
                                                                src={item.thumbnailUrl}
                                                            />
                                                        </MprcLink>
                                                    </Grid>
                                                    <Grid size="grow" overflow="hidden">
                                                        <Grid container alignItems="center" flexWrap="nowrap" width="100%">
                                                            <Grid overflow="hidden">
                                                                <MprcListItemTitle onClick={() => handleUserClick(item)}>
                                                                    {item.name}
                                                                </MprcListItemTitle>
                                                            </Grid>
                                                            <Grid>
                                                                <MprcIconButtonMoreOptionsDropdown
                                                                    color="muted"
                                                                    onChange={(selection) => handleSelectMenuOption(selection, item)}
                                                                    items={getMenuItems()}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                        <Grid container alignItems="center" spacing={1} flexWrap="nowrap" width="100%">
                                                            <Grid mt={theme => theme.spacing(-.5)}>
                                                                {buildSecondLine(item)}
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        );
                                    })
                                    }
                                </Grid>
                            </>
                        )}
                </CardContent>
                {viewData && viewData.totalPages > 1 && (
                    <MprcCardActions>
                        <Pagination
                            shape="rounded"
                            color="primary"
                            page={viewData.pageNumber}
                            count={viewData.totalPages}
                            onChange={(e, pageNumber) => {
                                if (pageNumber != viewFilter.pageNumber) {
                                    setViewFilter({ ...viewFilter, pageNumber });
                                }
                            }}
                        />
                    </MprcCardActions>
                )}
            </>
        );
    };

    ///
    return (
        <>
            <MprcRequestErrorReport error={viewError} />

            <MprcRequestIndicator visible={posting} />

            {dialog}

            {
                !viewError &&
                <MprcContainer>
                    <MprcCard>
                        {
                            <>
                                <Filter
                                    filterOptions={viewFilter.filter}
                                    ref={searchTextField}
                                    onFilterChange={(filter) => setViewFilter({ ...viewFilter, filter })}
                                />
                                {renderContent()}
                            </>
                        }
                    </MprcCard>
                </MprcContainer>
            }
        </>
    );
};
